import * as React from 'react';

import { SidebarNavItem, ArticleNavItem } from './types';
import { SidebarContextProps, SidebarProvider } from './context';
import { AppNav } from './app-nav';
import { ArticleNav } from './article-nav';

import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface SidebarProps extends React.ComponentPropsWithoutRef<'aside'>, SidebarContextProps {
  showAppBack?: boolean;
  onAppBack?: () => void;
  appNavItems?: SidebarNavItem[];
  appNavActiveId?: string;
  showArticles?: boolean;
  isLoadingArticles?: boolean;
  articles?: ArticleNavItem[];
  sectionNavItems?: SidebarNavItem[];
  sectionNavActiveId?: string;
}

export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  {
    className,
    ItemLinkComponent,
    collapsed,
    showAppBack,
    onAppBack,
    appNavItems,
    appNavActiveId,
    isLoadingArticles,
    sectionNavItems,
    sectionNavActiveId,
    activeArticleId,
    showArticles,
    articles,
    onSelectArticle,
    onExpandArticle,
    onSearchArticles,
    onAddArticle,
    onMoreActions,
    canMoveArticle,
    onMoveArticle,
    ...props
  },
  ref,
) {
  return (
    <SidebarProvider
      collapsed={!!collapsed}
      ItemLinkComponent={ItemLinkComponent}
      activeArticleId={activeArticleId}
      onSelectArticle={onSelectArticle}
      onExpandArticle={onExpandArticle}
      onSearchArticles={onSearchArticles}
      onAddArticle={onAddArticle}
      onMoreActions={onMoreActions}
      canMoveArticle={canMoveArticle}
      onMoveArticle={onMoveArticle}
    >
      <aside
        ref={ref}
        {...props}
        className={cn(
          'py-4 transition-width duration-200 ease-in-out flex flex-col h-dvh',
          collapsed ? 'w-[64px]' : 'w-[260px]',
          className,
        )}
        aria-label="Application navigation"
      >
        <AppNav
          className={cn(
            'flex-shrink-0',
            appNavItems && appNavItems.length > 0 && 'pb-4',
            !collapsed && 'border-b border-border-subtle',
          )}
          showBack={showAppBack}
          items={appNavItems}
          activeId={appNavActiveId}
          onBack={onAppBack}
          sectionNavItems={sectionNavItems}
          sectionNavActiveId={sectionNavActiveId}
        />

        {!collapsed && (
          <>
            {showArticles && (
              <ScrollArea>
                <ArticleNav
                  className="py-4 max-w-[var(--left-nav-width,inherit)]"
                  articles={articles}
                  isLoadingArticles={isLoadingArticles}
                />
              </ScrollArea>
            )}
          </>
        )}
      </aside>
    </SidebarProvider>
  );
});
